import React from 'react';
import { motion } from 'framer-motion';
import AIVideoHeader from '../AIVideoHeader';
import AIVideoFooter from '../AIVideoFooter';
import VideoBackground from '../VideoBackground';
import videoCreationBg from '@/assets/video-creation-bg.jpg';

interface Step2VideoGoalProps {
  onNext: (answer: string) => void;
  onBack: () => void;
}

const Step2VideoGoal: React.FC<Step2VideoGoalProps> = ({ onNext, onBack }) => {
  const options = [
    { id: 'short-films', label: '🎬 Short Films & Movies', emoji: '🎬' },
    { id: 'video-ads', label: '📈 Video Ads for Products/Services', emoji: '📈' },
    { id: 'podcasts', label: '🎙️ Engaging Video Podcasts', emoji: '🎙️' },
    { id: 'faceless', label: '👤 Faceless YouTube/Social Content', emoji: '👤' },
    { id: 'ai-influencers', label: '✨ AI Influencers & Virtual Avatars', emoji: '✨' },
    { id: 'educational', label: '💡 General Explainer/Educational Videos', emoji: '💡' },
  ];

  const fallbackBackground = (
    <div className="absolute inset-0">
      <motion.div 
        className="absolute top-20 left-10 w-32 h-32 bg-gray-500/10 rounded-full blur-xl"
        animate={{
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-24 h-24 bg-gray-400/10 rounded-full blur-xl"
        animate={{
          x: [0, -40, 0],
          y: [0, 40, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900/20 to-black relative overflow-hidden flex flex-col">
      <VideoBackground
        videoSrc="https://res.cloudinary.com/drinkamrs/video/upload/v1752589518/Characters_the_trainee_202507140455_sf20iu.mp4"
        fallbackComponent={fallbackBackground}
        overlay={true}
      />
      <AIVideoHeader 
        currentStep={2} 
        totalSteps={8} 
        onBack={onBack} 
      />

      <div className="relative z-10 flex-1 px-4 py-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-sm mx-auto"
        >
          <motion.h2 
            className="text-2xl md:text-3xl font-bold text-white text-center mb-8 drop-shadow-lg font-playfair"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ textShadow: "0 2px 4px rgba(0,0,0,0.8)" }}
          >
            What kind of video are you most excited to create with{' '}
            <span className="bg-gradient-to-r from-hollywood-gold to-hollywood-shimmer bg-clip-text text-transparent drop-shadow-none">
              AI
            </span>
            ?
          </motion.h2>

          <div className="space-y-2.5">
            {options.map((option, index) => (
              <motion.button
                key={option.id}
                className="w-full p-3 text-left bg-black/30 backdrop-blur-md hover:bg-hollywood-gold/20 border border-hollywood-gold/30 hover:border-hollywood-gold-light/70 rounded-xl transition-all duration-300 group relative overflow-hidden shadow-lg"
                onClick={() => onNext(option.id)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-hollywood-gold/0 via-hollywood-gold-light/20 to-hollywood-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                />
                <div className="relative z-10 flex items-center gap-3">
                  <span className="text-xl">{option.emoji}</span>
                   <span className="font-bold text-white group-hover:text-hollywood-shimmer transition-colors duration-300 text-base">
                    {option.label.replace(option.emoji + ' ', '')}
                   </span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>

      <AIVideoFooter />
    </div>
  );
};

export default Step2VideoGoal;